import { Ref } from '@stardust-ui/react-component-ref';
import classNames from 'classnames/bind';
import Checkbox from 'mcs-toolkit/components/Checkbox';
import Form from 'mcs-toolkit/components/Form';
import React, { useRef } from 'react';
import { StrictFormFieldProps } from 'semantic-ui-react';
import { arrayHelper } from 'utils/arrayHelper';

import { CheckboxAdvanced } from 'reactApp/components/CheckboxAdvanced/CheckboxAdvanced';
import { QuestionTooltip } from 'reactComponents/questionTooltip/questionTooltip';
import { WarningMessage } from 'reactComponents/WarningMessage/WarningMessage';

import styles from './_BasicField.css';
import { _BasicFieldError } from './_BasicFieldError/_BasicFieldError';
import { FormikContext } from './FormikContext';
import { setIn } from './formUtils';

const css = classNames.bind(styles);

const CHECBKOX_CONTROLS = [CheckboxAdvanced, Checkbox];

type InfoMessage = React.ReactNode | (React.ReactNode | undefined)[];

export type BasicFieldProps = StrictFormFieldProps & {
    label?: string;
    onChange?: (
        event: React.SyntheticEvent,
        data: { value: any } & any,
        prevValue: any,
    ) => void;
    control: React.ComponentType;
    controlLabel?: string;
    name: string;
    hidden?: boolean;
    extraLabel?: string | React.ReactNode;
    innerRef?: Function;
    notErrorMessage?: React.ReactNode;
    infoMessage?: InfoMessage;
    hint?: React.ReactNode;
    hideErrorAtField?: boolean;
    description?: string;
    labelDescription?: React.ReactNode;
    dataTest?: string;
    className?: string; // pass to control
    rootClassName?: string; // pass to basic field root
    fieldClassName?: string; // don't use TODO: remove & move styles to theme
    wrapperClassName?: string; // don't use TODO: remove & move styles to theme
    labelClassName?: string; // don't use TODO: remove & move styles to theme
    errorClassName?: string; // don't use TODO: remove & move styles to theme
    setTouchedOnChange?: boolean;
};

export const BasicField = ({
    label,
    onChange,
    control,
    controlLabel,
    name,
    hidden,
    extraLabel,
    innerRef,
    notErrorMessage,
    infoMessage,
    rootClassName,
    fieldClassName,
    wrapperClassName,
    labelClassName,
    errorClassName,
    hint,
    hideErrorAtField,
    labelDescription,
    dataTest,
    setTouchedOnChange,
    ...semanticFieldProps
}: BasicFieldProps) => {
    const newValues = useRef();
    return (
        <FormikContext.Consumer>
            {// eslint-disable-next-line
        (context: any) => {
                const {
                    values = {},
                    setFieldValue,
                    setFieldTouched,
                    setFieldChanged,
                    theme = 'default',
                    submitCount,
                    getFieldMeta,
                } = context;

                const { value, error, touched } = getFieldMeta
                    ? getFieldMeta(name)
                    : ({} as any); // when field used out of form
                // TODO: remove cases when field used out of form

                const showError = Boolean(
                    (submitCount || touched) &&
                        error &&
                        typeof error === 'string',
                );

                const isCheckbox = CHECBKOX_CONTROLS.includes(control);

                const showNotError = !showError && notErrorMessage;

                const handleBlur = (event, props) => {
                    const name =
                        event.target.name ||
                        event.target.getAttribute('name') ||
                        (props && props.name);
                    if (name) {
                        setFieldTouched(name, true, false);
                        if (context.validateOnBlur) {
                            context.validateForm(newValues.current);
                        }
                    }
                };
                const handleChange = (event, data: any) => {
                    if (!name) {
                        if (typeof onChange === 'function') {
                            onChange(event, data, data.value);
                        }
                    } else {
                        const { value: prevValue } = getFieldMeta(name);

                        let value = data;
                        if (data && data.name === name) {
                            value = isCheckbox ? data.checked : data.value;
                        }

                        setFieldValue(name, value);
                        setFieldChanged(name);

                        if (setTouchedOnChange) {
                            setFieldTouched(name, true, false);
                        }

                        newValues.current = setIn(values, name, value);

                        if (
                            context.validateOnChange ||
                            (context.validateOnBlur &&
                                (touched || setTouchedOnChange))
                        ) {
                            context.validateForm(newValues.current);
                        }

                        if (typeof onChange === 'function') {
                            onChange(event, data, prevValue);
                        }
                    }
                };

                const fieldProps = {
                    className: css('field'),
                    error: showError,
                    children: React.createElement(control, {
                        [isCheckbox ? 'checked' : 'value']: value,
                        label: controlLabel,
                        onChange: handleChange,
                        onBlur: handleBlur,
                        className: fieldClassName,

                        error: hideErrorAtField && showError && error,

                        name,
                        id: name,

                        ...semanticFieldProps,
                        ...(dataTest && { dataTest }),
                    }),
                };

                const hasVisibleError = !hideErrorAtField && showError && error;

                return (
                    !hidden && (
                        <div
                            className={css(
                                `${theme}Theme`,
                                'root',
                                rootClassName,
                                {
                                    disabled: semanticFieldProps.disabled,
                                    hasVisibleError,
                                },
                            )}
                        >
                            <div
                                className={css(
                                    'fieldWrapper',
                                    wrapperClassName,
                                )}
                            >
                                {label && (
                                    <div className={css('labelContainer')}>
                                        <label
                                            className={css(
                                                'label',
                                                labelClassName,
                                            )}
                                        >
                                            {label}
                                        </label>
                                        {labelDescription && (
                                            <QuestionTooltip
                                                triggerClassName={css(
                                                    'labelDescription',
                                                )}
                                                text={labelDescription}
                                                position="right center"
                                            />
                                        )}
                                    </div>
                                )}

                                {innerRef ? (
                                    <Ref innerRef={innerRef as any}>
                                        <Form.Field {...fieldProps} />
                                    </Ref>
                                ) : (
                                    <Form.Field {...fieldProps} />
                                )}

                                {extraLabel && (
                                    <div
                                        className={css(
                                            'extraLabelWrapper',
                                            'extraInfoWrapper',
                                        )}
                                    >
                                        <div className={css('extraLabel')}>
                                            {extraLabel}
                                        </div>
                                    </div>
                                )}
                            </div>
                            {!hideErrorAtField && (
                                <div
                                    className={css(
                                        'errorWrapper',
                                        errorClassName,
                                    )}
                                >
                                    {showError && (
                                        <_BasicFieldError
                                            className={css('message')}
                                            dataTest={`${name}-validation-errors`}
                                        >
                                            {error}
                                        </_BasicFieldError>
                                    )}
                                    {showNotError && (
                                        <div
                                            className={css(
                                                'message',
                                                'notErrorMessage',
                                            )}
                                        >
                                            {notErrorMessage}
                                        </div>
                                    )}
                                </div>
                            )}
                            {hint && !showError && (
                                <div className={css('hint')}>{hint}</div>
                            )}
                            {renderInfoMessages(infoMessage)}
                        </div>
                    )
                );
            }}
        </FormikContext.Consumer>
    );
};

function renderInfoMessages(infoMessage: InfoMessage) {
    const messages = arrayHelper.clearNullable(
        arrayHelper.singleToArray(infoMessage),
    );

    if (!messages.length) {
        return null;
    }

    return (
        <div className={css('infoMessagesWrapper')}>
            {messages.map((node, index) => (
                <WarningMessage
                    lessPadding
                    className={css('infoMessage')}
                    key={index}
                >
                    {node}
                </WarningMessage>
            ))}
        </div>
    );
}
